// What the pull request page reads beside the pull request and keeps for a
// minute: the titles of its related issues (the Links card) and the
// deployments of its latest commits (previews). The page reloads on every
// live update, so each is read again with a reload only once the record
// kept is KEPT_MS old; the deployments also when the head moved (their
// record is per head).
//
// The records are a runtime cache (runtime/cache.ts) in this page's
// memory, one per store: a pull request opened again is read afresh (as
// the `opened` event of the account's log would have it for a stored
// record), so nothing would be gained by storing them.
import { graphql } from '../../api/gh';
import { identityUser } from '../../api/identity';
import type { KnownRef } from '../../logic/links';
import type { DeploymentCommit } from '../../logic/previews';
import { cacheKey, hashInputs, memoryBackend, openCache } from '../../runtime/cache';
import { loadKnownRefs, type Titles } from '../related';
import { fetchDeployments, type PrAt } from './fetch';
import { KEPT, type Kept } from './program';

/** The least time between two reads of a pull request's related-issue titles, or of its deployments at one head. */
export const KEPT_MS = 60_000;

const list = (raw: unknown): unknown[] | null => (Array.isArray(raw) ? raw : null);

export interface SideReads {
  refs(signal: AbortSignal): Promise<Titles | Kept>;
  deployments(head: string | null, signal: AbortSignal): Promise<DeploymentCommit[] | Kept>;
}

/** The kept reads of one pull request page (see the file comment). */
export function sideReads(at: PrAt, now: () => number = Date.now): SideReads {
  const key = cacheKey({
    origin: typeof location === 'undefined' ? 'page' : location.origin,
    account: identityUser() ?? 'nobody',
    schemaVersion: 1,
    inputsHash: hashInputs(),
  });
  const backend = memoryBackend();
  const refs = openCache<KnownRef[]>({ name: 'pr-refs', key, decode: (raw) => list(raw) as KnownRef[] | null, backend, maxAgeMs: KEPT_MS, now });
  const deployments = openCache<DeploymentCommit[]>({
    name: 'pr-deployments',
    key,
    decode: (raw) => list(raw) as DeploymentCommit[] | null,
    backend,
    maxAgeMs: KEPT_MS,
    now,
  });
  return {
    async refs(signal) {
      if (await refs.get('refs')) return KEPT;
      const ticket = refs.begin();
      const titles = await loadKnownRefs((query, variables) => graphql(query, variables, { signal }), { owner: at.owner, repo: at.repo, number: at.number });
      if (titles.kind === 'read') await refs.write({ refs: titles.refs }, ticket);
      return titles;
    },
    async deployments(head, signal) {
      const k = head ?? '';
      if (await deployments.get(k)) return KEPT;
      const ticket = deployments.begin();
      const read = await fetchDeployments(at, signal);
      // One head's record at a time: the head before is not asked for again.
      const others = Object.fromEntries([...(await deployments.entries()).keys()].filter((x) => x !== k).map((x) => [x, null]));
      await deployments.write({ ...others, [k]: read }, ticket);
      return read;
    },
  };
}
